import React from 'react';
import PropTypes, { func } from 'prop-types';

import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Field,
  Textarea,
  mergeClasses,
  ProgressBar,
  Divider,
  Subtitle2,
  Body2,
  tokens,
} from '@fluentui/react-components';
import {
  AddAssetEntryCard,
  EntryCardSVGIcons,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { Dismiss20Regular, StyleGuide24Regular } from '@fluentui/react-icons';

import { useCommonDialogueStyles } from './style';
import { FileUpload } from '../../../file-upload';
import { WarningDialogueContent } from './warning-dialogue-content';

const UploadBrandGuideDialogueContent = ({
  i18n,
  brandKitCreationPayload,
  isNewBrandCreateWarningEnabled,
  onChangeBrandKitCreationPayload,
}) => {
  const commonClasses = useCommonDialogueStyles();

  const handleDescriptionChange = (e) => {
    onChangeBrandKitCreationPayload({
      ...brandKitCreationPayload,
      description: e.target.value,
    });
  };

  const handleFilesUpload = (files) => {
    onChangeBrandKitCreationPayload({
      ...brandKitCreationPayload,
      files,
    });
  };

  if (isNewBrandCreateWarningEnabled) {
    return <WarningDialogueContent i18n={i18n} />;
  }

  return (
    <div className={commonClasses.dialogueContentContainer}>
      <Field
        label={{
          size: 'small',
          weight: 'semibold',
          children: i18n.getString(_TL_('Add Brand Guidance File:')),
        }}
      >
        <FileUpload
          i18n={i18n}
          disableAddMore
          previewFilesPerRow={2}
          maxFilesCountAllowed={1}
          maxFileSize={10 * 1024 * 1024}
          onFilesUploaded={handleFilesUpload}
          allowedTypes={['.pdf', '.doc', '.docx', '.ppt', '.pptx']}
          dragOrDropTip={_TL_('File Format: PDF, Word, PPT . Maximum file size: 10 MB')}
        />
      </Field>
      <Field
        label={{
          size: 'small',
          weight: 'semibold',
          children: i18n.getString(_TL_('What’s your goal for creating a new kit? This helps us better align with your brand.')),
        }}
      >
        <div className={commonClasses.searchBoxWrapper}>
          <Textarea
            size="medium"
            appearance="outline"
            className={mergeClasses('search-area', commonClasses.searchBox)}
            placeholder={i18n.getString(_TL_('Enter description'))}
            value={brandKitCreationPayload.description}
            onChange={handleDescriptionChange}
          />
        </div>
      </Field>
    </div>
  );
};

UploadBrandGuideDialogueContent.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  brandKitCreationPayload: PropTypes.shape({
    files: PropTypes.arrayOf(PropTypes.shape({})),
    description: PropTypes.string,
  }).isRequired,
  onChangeBrandKitCreationPayload: PropTypes.func.isRequired,
  isNewBrandCreateWarningEnabled: PropTypes.bool.isRequired,
};

const UploadBrandGuideDialogue = ({
  i18n,
  onCreateBrandKit,
  disableNewBrandKitWarning,
  isBrandKitCreationInProgress,
}) => {
  const commonClasses = useCommonDialogueStyles();
  const [isDialogueOpen, setIsDialogueOpen] = React.useState(false);
  const [
    isNewBrandCreateWarningEnabled,
    setNewBrandCreateWarningEnabled,
  ] = React.useState(!disableNewBrandKitWarning);
  const [brandKitCreationPayload, setBrandKitCreationPayload] = React.useState({
    files: [],
    description: '',
  });

  const handleDialogueOpenChange = (dialogueData) => {
    setIsDialogueOpen(dialogueData.open);
    if (!dialogueData.open) {
      setBrandKitCreationPayload({
        files: [],
        description: '',
      });
      setNewBrandCreateWarningEnabled(true);
    }
  };

  const handleCreateBrandKit = () => {
    if (brandKitCreationPayload.files.length === 0) {
      return;
    }
    onCreateBrandKit(brandKitCreationPayload);
    console.info('Creating brand kit with payload:', brandKitCreationPayload);
  };

  return (
    <Dialog open={isDialogueOpen} onOpenChange={(_e, data) => handleDialogueOpenChange(data)}>
      <DialogTrigger disableButtonEnhancement>
        <AddAssetEntryCard
          cardClassName="upload-brand-guide-card"
          onClick={() => setIsDialogueOpen(true)}
          description={i18n.getString(_TL_('Upload Brand Guidance File'))}
          title={i18n.getString(_TL_('Create with Brand Guide'))}
          imageSrc={EntryCardSVGIcons.UploadSVGIcon}
          isFillWidth
        />
      </DialogTrigger>
      <DialogSurface className={mergeClasses(
        'ads-studio-banner-upload-brand-guide-dialogue',
        commonClasses.container
        )}
      >
        <DialogBody>
          <DialogTitle>
            <div className={commonClasses.header}>
              <div>
                <StyleGuide24Regular />
                <Subtitle2 className="header-title">
                  {isNewBrandCreateWarningEnabled
                    ? i18n.getString(_TL_('Create New Brand Kit'))
                    : i18n.getString(_TL_('Create Brand Kit with Brand Documentation'))}
                </Subtitle2>
              </div>
              <DialogTrigger disableButtonEnhancement>
                <Dismiss20Regular
                  className={mergeClasses(
                    'close-dialogue-button',
                    commonClasses.dismissIcon
                  )}
                />
              </DialogTrigger>
            </div>
            <Divider className={commonClasses.horizontalDivider} />
          </DialogTitle>
          <DialogContent>
            <UploadBrandGuideDialogueContent
              i18n={i18n}
              brandKitCreationPayload={brandKitCreationPayload}
              onChangeBrandKitCreationPayload={setBrandKitCreationPayload}
              isNewBrandCreateWarningEnabled={isNewBrandCreateWarningEnabled}
            />
            {isBrandKitCreationInProgress && (
              <>
                <Body2
                  className={commonClasses.loadingText}
                  color={tokens.colorNeutralStroke1}
                >
                  {i18n.getString(_TL_('Finding brand elements...'))}
                </Body2>
                <div className={commonClasses.loadingContainer}>
                  <ProgressBar thickness="large" />
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions
            className={
              isNewBrandCreateWarningEnabled
                ? commonClasses.warningFooter
                : commonClasses.footer
            }
          >
            {isNewBrandCreateWarningEnabled ? (
              <Button
                appearance="primary"
                className="create-new-kit-button"
                onClick={() => setNewBrandCreateWarningEnabled(false)}
              >
                {i18n.getString(_TL_('Create new kit'))}
              </Button>
            ) : (
              <Button
                appearance="primary"
                onClick={handleCreateBrandKit}
                className="create-brand-kit-button"
                disabled={brandKitCreationPayload.files.length < 1}
              >
                {i18n.getString(_TL_('Create'))}
              </Button>
            )}
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" className="cancel-dialogue-button">
                {i18n.getString(_TL_('Cancel'))}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export { UploadBrandGuideDialogue };

UploadBrandGuideDialogue.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  disableNewBrandKitWarning: PropTypes.bool,
  onCreateBrandKit: PropTypes.func.isRequired,
  isBrandKitCreationInProgress: PropTypes.bool,
};

UploadBrandGuideDialogue.defaultProps = {
  disableNewBrandKitWarning: false,
  isBrandKitCreationInProgress: false,
};
